import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { useMutation } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import type {
  AgentPlaygroundFormValues,
  PlaygroundSessionState,
} from "src/api/agents/execution";
import {
  useAgentExecutionClient,
  useAgentSession,
} from "src/api/agents/execution";
import type {
  AgentInvokeResponse,
  AgentPlaygroundInvokeRequest,
  SessionResponse,
} from "src/clients/agent-management";

const PREWARM_COOLDOWN_MS = 20_000; // dedup window in ms

/**
 * Module-level storage for playground IDs.
 * - Fresh ID generated on each page load
 * - Automatically reset on page refresh
 * - Isolated per browser tab
 */
const playgroundIdMap = new Map<string, string>();

/**
 * Module-level ping promise tracking.
 * Tracks in-flight ping requests per agent/version to enable invoke queueing.
 * When a ping is in-flight, invoke requests wait for it to complete
 * to avoid concurrent requests to the same container session.
 */
const pingPromiseMap = new Map<string, Promise<void>>();

/**
 * Subscription infrastructure for ping state.
 * Allows React components to subscribe to ping state changes via useSyncExternalStore.
 */
const pingListeners = new Set<() => void>();

const subscribeToPingState = (callback: () => void): (() => void) => {
  pingListeners.add(callback);
  return () => pingListeners.delete(callback);
};

const notifyPingListeners = (): void => {
  pingListeners.forEach((cb) => cb());
};

const getOrCreatePlaygroundId = (
  agentId: string,
  versionId: string,
): string => {
  const key = `${agentId}-${versionId}`;
  let id = playgroundIdMap.get(key);
  if (!id) {
    id = uuidv4();
    playgroundIdMap.set(key, id);
  }
  return id;
};

const resetPlaygroundId = (agentId: string, versionId: string): string => {
  const key = `${agentId}-${versionId}`;
  const newId = uuidv4();
  playgroundIdMap.set(key, newId);
  return newId;
};

type PlaygroundInvokeParams = {
  inputSchema: Record<string, unknown> | null;
  submittedInput: AgentPlaygroundFormValues;
};

/**
 * Internal session state machine for tracking the client-side session lifecycle.
 * This is the root state managed by the client. {@link PlaygroundSessionState} is
 * derived from this combined with server-side session data.
 * - idle: No session active
 * - active: Session in progress (running, paused, or completed)
 * - stopped: UI stopped tracking, wants to be able to start a new run
 */
type InternalSessionState =
  | { type: "idle" }
  | {
      type: "active";
      sessionId: string;
      inputSchema: Record<string, unknown> | null;
      submittedInput: AgentPlaygroundFormValues;
    }
  | {
      type: "stopped";
      sessionId: string;
      inputSchema: Record<string, unknown> | null;
      submittedInput: AgentPlaygroundFormValues;
    };

/**
 * Invoke mutation interface exposed by the context.
 * Simplified from full UseMutationResult to only what consumers need.
 */
type PlaygroundInvokeMutation = {
  mutate: (
    params: PlaygroundInvokeParams,
    options?: { onError?: () => void },
  ) => void;
  isPending: boolean;
};

type PlaygroundContextValue = {
  playgroundId: string;
  sessionState: PlaygroundSessionState;
  /** Raw session data from the API, useful for debugging/copy features */
  rawSessionData: SessionResponse | undefined;
  ping: () => void;
  /**
   * Stops the current session UI-side only.
   * The backend session continues, but we stop polling and allow input editing.
   * Messages remain visible.
   */
  stopSession: () => void;
  invoke: PlaygroundInvokeMutation;
  /**
   * Register a start test handler from AgentPlayground.
   * This allows the header button to trigger form validation and submission.
   */
  registerStartTest: (handler: () => void) => void;
  /**
   * Trigger the registered start test handler.
   * Called by the header button when playground is open but not running.
   */
  startTest: () => void;
};

const PlaygroundContext = createContext<PlaygroundContextValue | null>(null);

type PlaygroundProviderProps = {
  agentId: string;
  versionId: string;
  children: React.ReactNode;
};

/**
 * Provides playground session state to children.
 *
 * - Fresh playground ID generated on each page load
 * - Isolated per browser tab
 * - Only regenerates on explicit resetSession() call or page refresh
 * - Provides ping() that sends the playground ID with prewarm requests
 * - Uses a cooldown window to deduplicate rapid ping triggers
 * - Tracks in-flight pings to enable invoke queueing (prevents 502 errors from agent runner)
 * - Exposes frontend session status for UI display (e.g. "Booting..." vs "Thinking...")
 */
export const PlaygroundProvider: React.FC<PlaygroundProviderProps> = ({
  agentId,
  versionId,
  children,
}) => {
  const { client } = useAgentExecutionClient();
  const lastPingRef = useRef<number>(0);
  const key = `${agentId}-${versionId}`;

  // Track ping in-flight state for UI via external store subscription.
  // We use useSyncExternalStore instead of useState to avoid stale setter issues:
  // if user navigates away while ping is in-flight, the .finally() callback would
  // call the unmounted component's setter, leaving new component's state stale.
  const isPingInFlight = useSyncExternalStore(
    subscribeToPingState,
    () => pingPromiseMap.has(key),
    () => false,
  );

  const firePing = useCallback(
    (targetPlaygroundId: string) => {
      if (!client) return;

      lastPingRef.current = Date.now();

      const pingPromise = client
        .playgroundPingAgentsManagementApiV0PlaygroundPingPost({
          playgroundPingRequest: {
            agent_id: agentId,
            agent_version_id: versionId,
            playground_id: targetPlaygroundId,
          },
        })
        .catch(() => {}) // Swallow errors
        .finally(() => {
          if (pingPromiseMap.get(key) !== pingPromise) {
            // Outdated/stale promise, don't clean up the newer one
            return;
          }
          pingPromiseMap.delete(key);
          notifyPingListeners();
        }) as unknown as Promise<void>;

      pingPromiseMap.set(key, pingPromise);
      notifyPingListeners();
    },
    [client, agentId, versionId, key],
  );

  // Internal session state machine: idle | active | stopped
  const [internalState, setInternalState] = useState<InternalSessionState>({
    type: "idle",
  });

  // Module-level storage ensures all components share the same ID
  // Fresh ID on page load, stable within session
  const [playgroundId, setPlaygroundId] = useState<string>(() =>
    getOrCreatePlaygroundId(agentId, versionId),
  );

  // Query session data - always pass sessionId to preserve cached data,
  // but only poll when session is active (not when stopped)
  const shouldPoll = internalState.type === "active";
  const currentSessionId =
    internalState.type !== "idle" ? internalState.sessionId : null;
  const sessionQuery = useAgentSession(currentSessionId, {
    polling: shouldPoll,
  });

  // Compute the unified PlaygroundSessionState from internal state and query data
  const sessionState: PlaygroundSessionState = (() => {
    const messages = sessionQuery.data?.messages ?? [];
    const serverStatus = sessionQuery.data?.status ?? null;

    if (internalState.type === "idle") {
      // No session - idle state
      return { status: "idle" as const };
    }

    const baseState = {
      sessionId: internalState.sessionId,
      sessionMessages: messages,
      inputSchema: internalState.inputSchema,
      submittedInput: internalState.submittedInput,
    };

    if (internalState.type === "stopped") {
      // UI stopped tracking - cancelled substatus
      return {
        ...baseState,
        status: "stopped",
        substatus: "cancelled",
      };
    }

    // internalState.type === "active"
    // Map server status to our status/substatus model
    if (serverStatus === "success") {
      const result = sessionQuery.data?.result;
      if (result) {
        return {
          ...baseState,
          status: "stopped",
          substatus: "success",
          result,
        };
      }
    }

    if (serverStatus === "failed") {
      return {
        ...baseState,
        status: "stopped",
        substatus: "failed",
        terminationReason: sessionQuery.data?.termination_reason ?? "error",
        terminationMessage: sessionQuery.data?.termination_message ?? null,
      };
    }

    if (serverStatus === "cancelled") {
      return {
        ...baseState,
        status: "stopped",
        substatus: "cancelled",
      };
    }

    if (serverStatus === "paused") {
      return {
        ...baseState,
        status: "active",
        substatus: "paused",
      };
    }

    // For pending, running, or no server status yet
    // Check if we're still booting (ping in flight and no server response)
    if (isPingInFlight && !serverStatus) {
      return {
        ...baseState,
        status: "active",
        substatus: "booting",
      };
    }

    // Default to running (covers 'pending', 'running', or optimistic state)
    return {
      ...baseState,
      status: "active",
      substatus: "running",
    };
  })();

  const ping = useCallback(() => {
    // Skip if ping already in-flight
    if (pingPromiseMap.has(key)) {
      return;
    }

    // Dedupe: skip if pinged within cooldown window
    if (Date.now() - lastPingRef.current < PREWARM_COOLDOWN_MS) {
      return;
    }

    firePing(playgroundId);
  }, [key, playgroundId, firePing]);

  /**
   * Stops the current session UI-side only.
   * The backend session continues, but we mark it as stopped so the UI
   * allows input editing while keeping messages visible.
   * Also generates a new playground ID and pings immediately to warm
   * the container for the next session.
   */
  const stopSession = useCallback(() => {
    if (internalState.type === "active") {
      // Generate new playground ID for next session
      const newPlaygroundId = resetPlaygroundId(agentId, versionId);
      setPlaygroundId(newPlaygroundId);

      // Ping immediately to warm container (bypass cooldown)
      firePing(newPlaygroundId);

      setInternalState({
        type: "stopped",
        sessionId: internalState.sessionId,
        inputSchema: internalState.inputSchema,
        submittedInput: internalState.submittedInput,
      });
    }
  }, [internalState, agentId, versionId, firePing]);

  // Ref to store the registered start test handler from AgentPlayground
  const startTestHandlerRef = useRef<(() => void) | null>(null);

  const registerStartTest = useCallback((handler: () => void) => {
    startTestHandlerRef.current = handler;
  }, []);

  const startTest = useCallback(() => {
    startTestHandlerRef.current?.();
  }, []);

  /**
   * Waits for any in-flight ping to complete before resolving.
   * Used by invoke to ensure sequential requests to AgentCore during coldstarts.
   */
  const waitForPingIfAny = useCallback(async (): Promise<void> => {
    const currentPingPromise = pingPromiseMap.get(key);
    if (currentPingPromise) {
      await currentPingPromise;
    }
  }, [key]);

  // Internal params type that includes generated IDs
  type InternalInvokeParams = PlaygroundInvokeParams & {
    sessionId: string;
    playgroundId: string;
  };

  // Internal mutation that receives all params including generated IDs
  const { mutate: internalMutate, isPending } = useMutation({
    mutationFn: async (
      params: InternalInvokeParams,
    ): Promise<AgentInvokeResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }

      // Wait for any in-flight ping to complete first
      await waitForPingIfAny();

      const { structuredInput, instructions } = params.submittedInput;

      const requestData: AgentPlaygroundInvokeRequest = {
        agent_id: agentId,
        agent_version_id: versionId,
        invocation_message: instructions,
        playground_id: params.playgroundId,
        session_id: params.sessionId,
      };

      // Only add structured_input if it has at least 1 field
      if (Object.keys(structuredInput).length > 0) {
        requestData.structured_input = structuredInput;
      }

      const response =
        await client.playgroundInvokeAgentAgentsManagementApiV0PlaygroundInvokePost(
          {
            agentPlaygroundInvokeRequest: requestData,
          },
        );

      return response.data;
    },
    onError: () => {
      // Clear session on error so user can retry
      setInternalState({ type: "idle" });
    },
  });

  // Wrapper that generates IDs and updates state before calling the mutation
  const invoke: PlaygroundInvokeMutation = useMemo(
    () => ({
      isPending,
      mutate: (
        params: PlaygroundInvokeParams,
        options?: { onError?: () => void },
      ) => {
        // Generate sessionId and set internal state immediately
        const sessionId = uuidv4();
        setInternalState({
          type: "active",
          sessionId,
          inputSchema: params.inputSchema,
          submittedInput: params.submittedInput,
        });

        // Call mutation with all params including generated IDs
        internalMutate({ ...params, sessionId, playgroundId }, options);
      },
    }),
    [internalMutate, isPending, playgroundId],
  );

  return (
    <PlaygroundContext.Provider
      value={{
        playgroundId,
        sessionState,
        rawSessionData: sessionQuery.data,
        ping,
        stopSession,
        invoke,
        registerStartTest,
        startTest,
      }}
    >
      {children}
    </PlaygroundContext.Provider>
  );
};

/**
 * Hook to access playground session state. Must be used within a PlaygroundProvider.
 */
export const usePlaygroundContext = (): PlaygroundContextValue => {
  const context = useContext(PlaygroundContext);
  if (!context) {
    throw new Error(
      "usePlaygroundContext must be used within PlaygroundProvider",
    );
  }
  return context;
};

/**
 * Reset module-level state for testing purposes.
 */
export const __resetPlaygroundState = (): void => {
  playgroundIdMap.clear();
  pingPromiseMap.clear();
  pingListeners.clear();
};
